import {useParams} from 'react-router-dom'
import {parseISO} from 'date-fns'

import commonStyles from '../styles.module.scss'
import styles from './BorrowerProjectAboutIndicators.module.scss'
import {MeterStat, ProgressCircle, Text, Box, Period} from 'common/components'
import {formatMoney, countPercentage} from 'common/helpers'
import {useGetProject, isProjectRequest} from '..'

interface ChartProps {
  project: any
}

export default function BorrowerProjectAboutIndicators() {
  const params = useParams()
  const {data: project} = useGetProject(params.uuid)

  const isRequest = isProjectRequest(project)

  return (
    <div className={commonStyles.indicatorsContainer}>
      <div className={commonStyles.indicatorsGrid}>
        <div className={commonStyles.indicatorsStats}>
          {isRequest ? (
            <Box weight="medium" ml={30}>
              <Text mb={2}>Requested loan</Text>
              <Text size="2xl" mb={2}>
                {formatMoney(project.loanRequests[0].loanValueRequested, {
                  cents: true,
                })}
              </Text>
              <Text size="xs" color="grey">
                excluding interest & fees
              </Text>
            </Box>
          ) : (
            <>
              <MeterStat
                large
                type="secondGreen"
                title="Current balance"
                value={formatMoney(project.balance, {cents: true})}
                label={`${formatMoney(project.availableBalance, {
                  cents: true,
                })} available`}
              />
              <MeterStat
                greyTitle
                type="darkGreen"
                title="Repayment"
                value={formatMoney(project.repaid, {cents: true})}
              />
              <MeterStat
                type="lightGrey"
                title="Full facility"
                value={formatMoney(project.facilityLimit2, {cents: true})}
                label="" // "including interest & fees"
                tips={{
                  pos: 'right',
                  text: `Full Facility = Facility + Establishment fee + Estimated Line Fee + Estimated Interest`
                }}
              />
            </>
          )}
        </div>
        <Chart project={project} />
        <div className={commonStyles.indicatorsStats}>
          {isRequest ? (
            <Box weight="medium" align="right" mr={30}>
              <Text mb={2}>Requested loan</Text>
              <Text size="2xl" mb={2}>
                $0.00
              </Text>
              <Text size="xs" color="grey">
                including interest & fees
              </Text>
            </Box>
          ) : (
            <>
              <MeterStat
                large
                right
                type="turquoize"
                title="Drawn amount"
                value={formatMoney(project.withdrawn, {cents: true})}
                label={`${formatMoney(project.availableWithdrawal, {
                  cents: true,
                })} available`}
              />
              <MeterStat
                right
                greyTitle
                type="lightTurquoize"
                title="Pending drawdown"
                value={formatMoney(project.withdrawPending, {cents: true})}
              />
              <MeterStat
                right
                type="lightGrey"
                title="Loan"
                value={formatMoney(project.facilityLimit1, {cents: true})}
                label="" // "excluding interest & fees"
                tips={{
                  pos: 'left',
                  text: `Loan = Approved Loan Amount + Legal fee + Broker fee`
                }}
              />
            </>
          )}
        </div>
      </div>
      <div className={commonStyles.indicatorsPeriod}>
        <Period
          noCaption={isRequest}
          openDate={parseISO(project.openDate)}
          maturityDate={parseISO(project.maturityDate)}
          withOverdue={project.status !== 'closed'}
        />
      </div>
    </div>
  )
}

const Chart = ({project}: ChartProps) => {
  const withdrawnValue = countPercentage(project.withdrawn, project.facilityLimit1)

  return (
    <div className={styles.chart}>
      <ProgressCircle
        green
        animation
        value={countPercentage(project.balance, project.facilityLimit2)}
        extraValue={countPercentage(project.repaid, project.facilityLimit2)}
        radius={140}
        strokeWidth={18}
      />
      <ProgressCircle
        animation
        className={styles.progress}
        value={withdrawnValue}
        extraValue={countPercentage(
          project.withdrawPending,
          project.facilityLimit1
        )}
        radius={94}
        strokeWidth={18}
      >
        <Text weight="medium" size="xs" color="turquoizeDeep">
          drawn
        </Text>
        <Text weight="medium" size="2xl" color="turquoizeDeep">
          {withdrawnValue}%
        </Text>
      </ProgressCircle>
    </div>
  )
}
